// SPDX-License-Identifier: AGPL-3.0-or-later
// Large purchases: saving up for one by a date, or financing it.
import { levelPayment } from "./amortization.js";
import { addMonths, monthsUntil } from "./dates.js";
/**
 * A loan paid monthly with interest compounded monthly, as car and personal
 * loans in Canada usually are. `annualRate` is nominal, e.g. 0.0699.
 */
export function loanPayment(principal, annualRate, months) {
    if (principal <= 0 || months <= 0)
        return { payment: 0, totalInterest: 0, totalPaid: 0 };
    const rate = Math.max(0, annualRate) / 12;
    const payment = Math.round(levelPayment(principal, rate, months));
    // Walk the schedule so the interest matches what a lender charges, cent rounding included.
    let balance = principal;
    let totalInterest = 0;
    for (let k = 0; k < months && balance > 0; k++) {
        const interest = Math.round(balance * rate);
        totalInterest += interest;
        balance = Math.max(0, balance + interest - payment);
    }
    return { payment, totalInterest, totalPaid: principal + totalInterest };
}
const HORIZON_MONTHS = 600;
/** What the goals need set aside this month, all together. */
export function totalNeed(goals, today) {
    return goals.reduce((s, g) => s + needPerMonth(g, today), 0);
}
/** What a goal needs each month to be paid for by its date. Past its date, all of what is left. */
export function needPerMonth(goal, today) {
    const remaining = Math.max(0, goal.price - goal.saved);
    if (remaining === 0)
        return 0;
    const months = monthsUntil(today, goal.targetDate);
    return months === 0 ? remaining : Math.ceil(remaining / months);
}
/**
 * Shares a monthly set-aside among save-up goals, highest priority first
 * (the order given). Each month every goal first gets up to what it needs;
 * anything left goes to the first goals still short. When a goal is paid
 * for, its money moves down the list. Lower goals that get less than they
 * need finish later than planned, and `projected` says when.
 *
 * With `monthly` null the set-aside follows the goals: each month it is what
 * the goals not yet paid for need, so it shrinks as they are reached.
 */
export function planSavings(goals, monthly, today) {
    const needs = goals.map((g) => needPerMonth(g, today));
    const remaining = goals.map((g) => Math.max(0, g.price - g.saved));
    const funded = goals.map(() => 0);
    const done = remaining.map((r) => (r === 0 ? 0 : null));
    for (let month = 1; month <= HORIZON_MONTHS && done.some((d) => d === null); month++) {
        let pool = monthly ?? goals.reduce((s, _, i) => s + (done[i] === null ? needs[i] : 0), 0);
        if (pool <= 0)
            break;
        const give = (i, cap) => {
            const amount = Math.min(cap, remaining[i], pool);
            if (amount <= 0)
                return;
            remaining[i] -= amount;
            pool -= amount;
            if (month === 1)
                funded[i] += amount;
            if (remaining[i] === 0)
                done[i] = month;
        };
        goals.forEach((_, i) => give(i, needs[i]));
        goals.forEach((_, i) => give(i, Infinity));
    }
    return goals.map((g, i) => {
        const left = Math.max(0, g.price - g.saved);
        const percentComplete = g.price > 0 ? Math.min(100, Math.round((Math.min(g.saved, g.price) / g.price) * 10000) / 100) : 100;
        const month = done[i];
        // Money set aside this month counts as month 1, so a goal finished in month m is ready m - 1 months from now.
        const projected = left === 0 ? today : month === null ? null : addMonths(today, month - 1);
        const monthsLeft = monthsUntil(today, g.targetDate);
        let pace;
        if (left === 0)
            pace = "complete";
        else if (g.targetDate < today)
            pace = "overdue";
        else if (projected === null || projected > g.targetDate)
            pace = "behind";
        else if (month !== null && month < monthsLeft)
            pace = "ahead";
        else
            pace = "on-track";
        return { id: g.id, percentComplete, remaining: left, need: needs[i], funded: funded[i], projected, pace };
    });
}
//# sourceMappingURL=purchases.js.map